// PDF builders para las Plantillas SmartPay (engine=LEGACY) dentro de Reportes Personalizados.
//
// Nota de implementacion: los 4 reportes legacy que hoy tienen PDF (General, Metodos de Pago,
// Pre-Cuentas Eliminadas, Descuentos) generan su docDefinition dentro de closures privados de
// cada tab (src/components/reports/tabs/*.jsx), leyendo tanto la respuesta del API como estado
// de UI que no existe en este contexto (selectedEventOption, waiters, etc). En vez de tocar esos
// 4 componentes en produccion para extraer esa logica (riesgo de regresion que no podemos
// verificar visualmente en este entorno), este modulo reconstruye un PDF con el mismo estilo,
// logo y estructura de secciones (Montos totales / Informacion adicional) directamente a partir
// de la respuesta cruda de POST /report-templates/:id/run - mismos datos, misma presentacion,
// pero sin depender de los closures internos de los tabs legacy.
const LOGO_URL = "https://res.cloudinary.com/dk3x6ubyi/image/upload/v1682698324/hi2vihfdtgg98ybwbxnn.png";

const STYLES = {
  header: { fontSize: 16, bold: true, alignment: "center", margin: [0, 5, 0, 10] },
  titles: { fontSize: 14, bold: true, alignment: "center" },
  subheader: { fontSize: 12, bold: true, alignment: "left" },
  tableExample: { margin: [0, 5, 0, 15] },
  tableHeader3: { fillColor: "#266DBF", fontSize: 13, color: "white", bold: true },
};

const money = (n) => "$ " + (Number(n) || 0).toFixed(2);

function baseDocDefinition(title, content) {
  return {
    pageSize: "A4",
    pageMargins: [23, 10, 23, 5],
    content: [
      { image: "logo-smartpay", width: 100 },
      { text: [title], style: "header" },
      ...content,
    ],
    images: { "logo-smartpay": LOGO_URL },
    styles: STYLES,
  };
}

function infoLine(label, value) {
  return { text: [{ text: label, style: "subheader" }, value || ""], margin: [0, 0, 0, 5] };
}

function totalsTable(title, rows) {
  return [
    { text: title, style: "titles" },
    {
      style: "tableExample",
      table: {
        widths: [200, "*"],
        headerRows: 1,
        body: [[{ text: "Dato", style: "tableHeader3" }, { text: "Total", style: "tableHeader3" }], ...rows],
      },
      layout: "lightHorizontalLines",
    },
  ];
}

function ordersTable(orders) {
  const body = [
    [
      { text: "Orden", style: "tableHeader3" },
      { text: "Fecha", style: "tableHeader3" },
      { text: "Total", style: "tableHeader3" },
      { text: "Impuesto", style: "tableHeader3" },
      { text: "Descuento", style: "tableHeader3" },
    ],
    ...(orders || []).map((o) => [
      String(o.transaction_code ?? ""),
      o.createdAt ? new Date(o.createdAt).toLocaleString("es-PA") : "",
      money(o.total),
      money(o.tax),
      money(o.discount),
    ]),
  ];
  return { style: "tableExample", table: { widths: ["*", "*", "*", "*", "*"], headerRows: 1, body }, layout: "lightHorizontalLines" };
}

// GENERAL / PAYMENT_METHOD / DISCOUNTS comparten la misma forma de respuesta (orders + analitics),
// asi que comparten el mismo builder de PDF - equivalente al patron de OrdersAnalyticsRenderer.
function buildOrdersAnalyticsPdf(title, payload, { filters, branchLabel }) {
  const analitics = payload?.analitics || {};
  const orders = payload?.orders || [];
  return baseDocDefinition(title, [
    infoLine("Empresa: ", analitics.business),
    infoLine("Sucursal: ", branchLabel),
    infoLine("Periodo: ", `${filters.minDate || ""} - ${filters.maxDate || ""}`),
    { text: "Ordenes", style: "titles", margin: [0, 15, 0, 6] },
    ordersTable(orders),
    ...totalsTable("Montos totales", [
      ["Subtotal: ", money(analitics.subTotalSales)],
      ["Impuestos: ", money(analitics.totalTaxSales)],
      ["Total de ventas: ", money(analitics.totalSales)],
    ]),
    ...totalsTable("Información adicional", [
      ["Devoluciones totales: ", money(analitics.totalDevolutions)],
      ["Descuentos totales: ", money(analitics.totalDiscounts)],
    ]),
  ]);
}

function buildGeneralPdf(payload, ctx) { return buildOrdersAnalyticsPdf("Reporte General", payload, ctx); }
function buildPaymentMethodPdf(payload, ctx) { return buildOrdersAnalyticsPdf("Reporte de Métodos de Pago", payload, ctx); }
function buildDiscountsPdf(payload, ctx) { return buildOrdersAnalyticsPdf("Reporte de Descuentos Aprobados", payload, ctx); }

function buildPreAccountPdf(payload, { filters, branchLabel }) {
  const analitics = payload?.analitics || {};
  const groups = analitics.branchofficeDate_orders || [];
  const content = [
    infoLine("Sucursal: ", branchLabel),
    infoLine("Periodo: ", `${filters.minDate || ""} - ${filters.maxDate || ""}`),
    ...totalsTable("Montos totales", [
      ["Subtotal: ", money(analitics.subTotalSales)],
      ["Impuestos: ", money(analitics.totalTaxSales)],
      ["Descuentos: ", money(analitics.totalDiscounts)],
      ["Devoluciones: ", money(analitics.totalDevolutions)],
      ["Total: ", money(analitics.totalSales)],
    ]),
  ];
  groups.forEach((group) => {
    content.push({ text: `Fecha de las pre cuentas: ${group.branchOfficeDate || ""}`, style: "titles", margin: [0, 12, 0, 6] });
    content.push(ordersTable(group.orders || []));
  });
  return baseDocDefinition("Reporte de Pre-Cuentas Eliminadas", content);
}

const PDF_BUILDERS = {
  GENERAL: buildGeneralPdf,
  PAYMENT_METHOD: buildPaymentMethodPdf,
  DISCOUNTS: buildDiscountsPdf,
  PRE_ACCOUNT: buildPreAccountPdf,
};

export function getLegacyPdfBuilder(reportCode) {
  return PDF_BUILDERS[reportCode] || null;
}
